'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import StarRating from '@/components/ui/StarRating';

const featuredMovies = [
{
  id: 'hero-001',
  title: 'Dune: Part Two',
  year: 2024,
  genre: ['Sci-Fi', 'Adventure', 'Drama'],
  rating: 4.6,
  reviewCount: 14820,
  synopsis: 'Paul Atreides unites with Chani and the Fremen while on a warpath of revenge against the conspirators who destroyed his family. Facing a choice between the love of his life and the fate of the known universe, he endeavors to prevent a terrible future only he can foresee.',
  director: 'Denis Villeneuve',
  runtime: 166,
  backdrop: "https://images.unsplash.com/photo-1675084190939-ac42684ec143",
  backdropAlt: 'Vast desert landscape with dramatic orange sky and rocky formations',
  badge: 'trending' as const
},
{
  id: 'hero-002',
  title: 'Oppenheimer',
  year: 2023,
  genre: ['Drama', 'History', 'Thriller'],
  rating: 4.8,
  reviewCount: 22340,
  synopsis: 'The story of American scientist J. Robert Oppenheimer and his role in the development of the atomic bomb during World War II. A biographical thriller exploring the moral weight of scientific discovery.',
  director: 'Christopher Nolan',
  runtime: 180,
  backdrop: "https://img.rocket.new/generatedImages/rocket_gen_img_1c5157876-1785350644575.png",
  backdropAlt: 'Abstract nuclear explosion light patterns with dramatic contrast',
  badge: 'top-rated' as const
},
{
  id: 'hero-003',
  title: 'Poor Things',
  year: 2023,
  genre: ['Comedy', 'Drama', 'Fantasy'],
  rating: 4.4,
  reviewCount: 9870,
  synopsis: 'The incredible tale and fantastical evolution of Bella Baxter, a young woman brought back to life by the brilliant and unorthodox scientist Dr. Godwin Baxter. Eager to learn, Bella runs off with lawyer Duncan Wedderburn on a whirlwind adventure across the continents.',
  director: 'Yorgos Lanthimos',
  runtime: 141,
  backdrop: "https://images.unsplash.com/photo-1724858611896-cee5c3eb795f",
  backdropAlt: 'Surreal colorful landscape with mountains and vibrant sky',
  badge: 'staff-pick' as const
}];


const badgeConfig = {
  trending: { label: 'Trending Now', className: 'badge-trending text-accent-foreground' },
  'top-rated': { label: 'Top Rated', className: 'bg-gold text-accent-foreground' },
  'staff-pick': { label: 'Staff Pick', className: 'badge-staff-pick text-white' },
  new: { label: 'New Release', className: 'badge-new text-white' }
};

export default function HeroBanner() {
  const [activeIndex, setActiveIndex] = useState(0);
  const featured = featuredMovies[activeIndex];

  return (
    <div className="relative w-full h-[70vh] min-h-[520px] max-h-[720px] overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0">
        <AppImage
          src={featured.backdrop}
          alt={featured.backdropAlt}
          fill
          priority
          className="object-cover transition-all duration-700"
          sizes="100vw" />
        
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 hero-overlay-bottom" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 h-full flex flex-col justify-end pb-16 pt-24">
        <div className="max-w-2xl fade-in" key={featured.id}>
          {/* Badge */}
          {featured.badge &&
          <span className={`inline-block px-3 py-1 rounded-full text-xs font-700 mb-4 ${badgeConfig[featured.badge]?.className}`}>
              {badgeConfig[featured.badge]?.label}
            </span>
          }

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-800 text-foreground leading-tight mb-2">
            {featured.title}
          </h1>

          <div className="flex items-center gap-4 mb-4 flex-wrap">
            <span className="text-muted-foreground text-sm">{featured.year}</span>
            <span className="text-muted-foreground text-sm">{featured.runtime}m</span>
            <div className="flex items-center gap-1.5">
              <StarRating value={featured.rating} size={14} showValue />
              <span className="text-xs text-muted-foreground">({featured.reviewCount.toLocaleString()} reviews)</span>
            </div>
            <div className="flex items-center gap-1.5">
              {featured.genre.map((g) =>
              <span key={`hero-genre-${g}`} className="text-xs px-2 py-0.5 rounded-full bg-white/10 text-foreground/80 border border-white/10">
                  {g}
                </span>
              )}
            </div>
          </div>

          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-6 line-clamp-3 max-w-xl">
            {featured.synopsis}
          </p>

          <p className="text-xs text-muted-foreground mb-6">
            Directed by <span className="text-foreground font-600">{featured.director}</span>
          </p>

          <div className="flex items-center gap-3 flex-wrap">
            <Link
              href="/movie-detail"
              className="btn-primary flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-700">
              
              <Icon name="PlayIcon" variant="solid" size={16} />
              Watch Now
            </Link>
            <Link
              href="/movie-detail"
              className="btn-ghost flex items-center gap-2 px-6 py-3 rounded-xl text-sm font-600">
              
              <Icon name="InformationCircleIcon" size={16} />
              More Info
            </Link>
            <button className="btn-ghost flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-600">
              <Icon name="PlusIcon" size={16} />
              Watchlist
            </button>
          </div>
        </div>

        {/* Slide selectors */}
        <div className="flex items-center gap-3 mt-8">
          {featuredMovies.map((m, i) =>
          <button
            key={`hero-dot-${m.id}`}
            onClick={() => setActiveIndex(i)}
            className={`transition-all duration-300 rounded-full ${
            i === activeIndex ?
            'w-8 h-2 bg-primary' : 'w-2 h-2 bg-white/30 hover:bg-white/60'}`
            }
            aria-label={`Show ${m.title}`} />

          )}
          <div className="ml-4 flex items-center gap-2">
            {featuredMovies.map((m, i) =>
            i !== activeIndex ? null : null
            )}
          </div>
        </div>

        {/* Mini film thumbnails */}
        <div className="absolute right-8 bottom-16 hidden xl:flex flex-col gap-2">
          {featuredMovies.map((m, i) =>
          <button
            key={`hero-thumb-${m.id}`}
            onClick={() => setActiveIndex(i)}
            className={`relative w-16 h-24 rounded-lg overflow-hidden transition-all duration-300 ${
            i === activeIndex ? 'ring-2 ring-primary scale-105' : 'opacity-50 hover:opacity-80'}`
            }>
            
              <AppImage
              src={m.backdrop}
              alt={m.backdropAlt}
              fill
              className="object-cover"
              sizes="64px" />
            
            </button>
          )}
        </div>
      </div>
    </div>);

}